import { Suspense, useEffect, useMemo, useState } from "preact/compat";

import "ckeditor5/ckeditor5-content.css";
import "./style.scss";

import Snackbar from "@mui/material/Snackbar";

import { JSX, VNode } from "preact";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "../../firebase";
import { useAutosave } from "react-autosave";

import "./youtube_iframe";
import { RouteComponentProps } from "src/declaration";
import { lazy } from "preact-iso";
import { Icon } from "../../lib/icons";

// TODO: add a CKEditor plugin that will popup a list of all images in firebase storage

type viewModes = "live" | "published" | "draft" | "editing";

interface ActionButton {
  icon: VNode;
  name: string;
  hide?: boolean;
  onClick: () => void;
}

export default function CMS(props: RouteComponentProps): JSX.Element {
  const [viewMode, _setViewMode] = useState<viewModes>("live");
  const [editorContent, setEditorContent] = useState<string>("");
  const [draftContent, setDraftContent] = useState<string>("");
  const [publishedContent, setPublishedContent] = useState<string>();

  const [snackbarMsg, setSnackbarMsg] = useState<string | null>();

  const encodedPath = encodeURIComponent(props.path?.replace(/\?.*/, ""));

  const canEdit =
    props.currentUser?.claims?.editor || props.currentUser?.claims?.admin;

  const saveDraft = (data: string, successMessage = "Draft autosaved") => {
    if (data !== draftContent) {
      setDoc(
        doc(db, "html-drafts", encodedPath),
        { body: data },
        { merge: true }
      ).then(() => setSnackbarMsg(successMessage));
      setDraftContent(data);
    }
  };

  const setViewMode = (value: viewModes, skipSnackbar = false): void => {
    _setViewMode(value);
    if (!skipSnackbar) {
      setSnackbarMsg("Now viewing: " + value);
    }
  };

  useAutosave({ data: editorContent, onSave: saveDraft });

  const init = (force = false) => {
    if (
      canEdit &&
      (force || !(draftContent || editorContent || publishedContent))
    ) {
      getDoc(doc(db, "html-drafts", encodedPath)).then((docSnap) => {
        if (docSnap.exists()) {
          setEditorContent(docSnap.data().body);
          setDraftContent(docSnap.data().body);
        } else {
          setEditorContent("");
          setDraftContent("");
        }
      });
      getDoc(doc(db, "html-published", encodedPath)).then((docSnap) => {
        if (docSnap.exists()) {
          setPublishedContent(docSnap.data().body);
        } else {
          setPublishedContent("");
        }
      });
    }
  };

  init();

  useEffect(() => {
    _setViewMode("live");
    init(true);
  }, [encodedPath]);

  const pageContent = () => {
    if (canEdit) {
      if (viewMode == "published") {
        if (publishedContent) {
          return publishedContent;
        }
      } else if (viewMode == "draft" || viewMode == "editing") {
        if (editorContent) {
          return editorContent;
        }
        if (draftContent) {
          return draftContent;
        }
      }
    }
    return (props?.innerHTML as unknown as string) || "";
  };

  const revertDraft = () => {
    const draft =
      publishedContent || (props?.innerHTML as unknown as string) || "";
    saveDraft(draft, "Discarded draft, restored to published version");
    setEditorContent(draft);
  };

  const saveDraftContent = () => {
    saveDraft(editorContent, "Draft saved");
  };

  const publishDraft = () => {
    const pageHistory = doc(
      db,
      "html-published",
      encodedPath,
      "history",
      new Date().toISOString()
    );
    setDoc(pageHistory, {
      author: props.currentUser?.name,
      body: draftContent,
      viewMode,
      propsURL: encodedPath,
      locationPath: encodeURIComponent(location.pathname),
    });

    setDoc(
      doc(db, "html-published", encodedPath),
      { body: draftContent },
      { merge: true }
    ).then(() => {
      setSnackbarMsg("Draft published");
      setPublishedContent(draftContent);
      setViewMode("published", true);
    });
  };

  const deployPublish = () => {
    alert("NYI");
  };

  const actions: Record<string, ActionButton> = {
    edit: {
      icon: <Icon name="ModeEditOutlineOutlinedIcon" />,
      name: "Edit Draft",
      onClick: () => setViewMode("editing"),
    },
    draft: {
      icon: <Icon name="DesignServicesOutlinedIcon" />,
      name: viewMode === "editing" ? "Finish Editing" : "View Draft",
      onClick: () => setViewMode("draft"),
    },
    live: {
      icon: <Icon name="DesignServicesOutlinedIcon" />,
      name: "View Live",
      onClick: () => setViewMode("live"),
    },
    published: {
      icon: <Icon name="DesignServicesOutlinedIcon" />,
      name: "View Published",
      onClick: () => setViewMode("published"),
    },
    revert: {
      icon: <Icon name="CancelOutlinedIcon" />,
      name: "Discard draft",
      onClick: revertDraft,
      hide: publishedContent === draftContent,
    },
    publish: {
      icon: <Icon name="SaveOutlinedIcon" />,
      name: "Publish draft",
      onClick: publishDraft,
      hide: publishedContent === draftContent,
    },
    save: {
      icon: <Icon name="SaveOutlinedIcon" />,
      name: "Save draft",
      onClick: saveDraftContent,
      hide: editorContent === draftContent,
    },
    deploy: {
      icon: <Icon name="SaveOutlinedIcon" />,
      name: "Deploy published",
      onClick: deployPublish,
    },
  };

  const editMenu = () => {
    const [editMenuOpen, setEditMenuOpen] = useState<boolean>(false);
    const availableActionNames = () => {
      if (viewMode === "draft") {
        return ["live", "published", "edit", "revert", "publish"];
      }
      if (viewMode === "editing") {
        return ["save", "draft", "revert", "publish"];
      }
      if (viewMode === "published") {
        return ["edit", "live", "draft"]; // , "deploy"
      }
      // Assume live is default
      return ["edit", "published", "draft"];
    };

    const availableActions = availableActionNames()
      .map((name) => actions[name])
      .filter((action) => !action.hide);

    const classes = ["editMenu", ...(editMenuOpen ? ["editMenuOpened"] : [])];

    return (
      <>
        <div
          class={classes.join(" ")}
          onClick={() => setEditMenuOpen(!editMenuOpen)}
        >
          <div key={"edit"} class="editMenuButton">
            <Icon name="EditIcon" />
            <br />
            Edit...
          </div>
          {availableActions.map((action) => (
            <div
              key={action.name}
              onClick={action.onClick}
              class="editMenuAction"
            >
              <div>
                {action.icon}
                <br />
                {action.name}
              </div>
            </div>
          ))}
        </div>
        <Snackbar
          open={!!snackbarMsg}
          onClose={() => setSnackbarMsg(null)}
          autoHideDuration={2000}
          message={snackbarMsg}
        />
      </>
    );
  };

  const EditWidget = lazy(() => import("./editor"));
  const onChange = (data: string) => {
    if (data !== editorContent) {
      setEditorContent(data);
    }
  };
  const editor = useMemo(
    () => (
      <Suspense fallback={"Loading..."}>
        <EditWidget pageContent={pageContent()} onChange={onChange} />
      </Suspense>
    ),
    []
  );

  const classes =
    "cmsContent" +
    " cms-" +
    props.path?.replace(/\W/, "") +
    (viewMode === "editing" ? " ck-editing" : " ck-content");

  if (typeof window === "undefined") {
    // pre-rendering - parser doesn't quite work right, so explicitly setting content. it comes from CMS/firestore anyway...
    return (
      <div class={classes}>
        <div dangerouslySetInnerHTML={{ __html: pageContent() }} />
      </div>
    );
  }

  return (
    <>
      {canEdit && editMenu()}
      <div class={classes} id="content-start">
        {(viewMode === "editing" && editor) || (
          <div dangerouslySetInnerHTML={{ __html: pageContent() }} />
        )}
      </div>
    </>
  );
}
